import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

export interface TranslationData {
  [key: string]: string | TranslationData;
}

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private http = inject(HttpClient);
  private currentLanguage$ = new BehaviorSubject<string>(environment.defaultLanguage);
  private translations = new Map<string, TranslationData>();
  private loadingTranslations = new Map<string, Observable<TranslationData>>();
  private loadedLanguages = new Set<string>();

  constructor() {
    this.loadLanguage(environment.defaultLanguage).subscribe();
  }

  loadLanguage(locale: string): Observable<TranslationData> {
    if (this.translations.has(locale)) {
      this.currentLanguage$.next(locale);
      return of(this.translations.get(locale)!);
    }

    if (this.loadingTranslations.has(locale)) {
      return this.loadingTranslations.get(locale)!;
    }

    const url = `${environment.basePath || ''}/assets/i18n/${locale}.json`;
    const load$ = this.http.get<TranslationData>(url).pipe(
      map((data) => {
        this.translations.set(locale, data);
        this.loadedLanguages.add(locale);
        this.currentLanguage$.next(locale);
        this.loadingTranslations.delete(locale);
        return data;
      }),
      catchError((error) => {
        console.error(`Erro ao carregar traduções para ${locale}:`, error);
        this.loadingTranslations.delete(locale);
        return of({});
      }),
      shareReplay(1)
    );

    this.loadingTranslations.set(locale, load$);
    return load$;
  }

  instant(key: string, params?: Record<string, string | number>): string {
    const locale = this.currentLanguage$.value;
    if (!this.loadedLanguages.has(locale)) {
      return key;
    }
    const translations = this.translations.get(locale) || {};
    const value = this.getNestedValue(translations, key);

    if (value === undefined) {
      console.warn(`Tradução não encontrada para a chave: ${key}`);
      return key;
    }

    return this.interpolate(value, params);
  }

  get(key: string, params?: Record<string, string | number>): Observable<string> {
    return this.currentLanguage$.pipe(
      map((locale) => {
        if (!this.loadedLanguages.has(locale)) {
          return key;
        }

        const translations = this.translations.get(locale) || {};
        const value = this.getNestedValue(translations, key);

        if (value === undefined) {
          console.warn(`Tradução não encontrada para a chave: ${key}`);
          return key;
        }

        return this.interpolate(value, params);
      })
    );
  }

  setLanguage(locale: string): Observable<void> {
    return new Observable((observer) => {
      this.loadLanguage(locale).subscribe({
        next: () => {
          localStorage.setItem('lang', locale);
          observer.next();
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLanguage$.asObservable();
  }

  getCurrentLanguageSync(): string {
    return this.currentLanguage$.value;
  }

  /** `undefined` = chave inexistente; `''` = tradução vazia válida. */
  private getNestedValue(obj: TranslationData, path: string): string | undefined {
    const keys = path.split('.');
    let value: string | TranslationData = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }

    return typeof value === 'string' ? value : undefined;
  }

  private interpolate(text: string, params?: Record<string, string | number>): string {
    if (!params) {
      return text;
    }

    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? String(params[key]) : match;
    });
  }
}

